import { Link } from "react-router-dom";
import { ArrowUpRight, Phone } from "lucide-react";
import { SERVICES, TESTIMONIALS, CONTACT } from "@/lib/services";
import Reveal from "@/components/Reveal";
import Seo, { LOCAL_BUSINESS_LD, WEBSITE_LD, breadcrumbLd } from "@/components/Seo";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1547609434-b732edfee020?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHw0fHxiZXNwb2tlJTIwY2FycGVudHJ5JTIwd29vZHdvcmtpbmclMjBjcmFmdHNtYW5zaGlwfGVufDB8fHx8MTc4MzAyNjA4OXww&ixlib=rb-4.1.0&q=85";

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="Joiners in Sheffield | Woodland Joinery & Building — Bespoke Joinery, Extensions & Staircases"
        description="Trusted Sheffield joiners specialising in bespoke joinery, extensions, staircases, fitted furniture, decking and full building work across Sheffield, Rotherham, Chesterfield and beyond. Free quotes."
        path="/"
        jsonLd={[
          LOCAL_BUSINESS_LD,
          WEBSITE_LD,
          breadcrumbLd([{ name: "Home", path: "/" }]),
        ]}
      />
      {/* Hero */}
      <section
        data-testid="hero-section"
        className="relative min-h-[100vh] flex items-end overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Master joiner working timber"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32 pt-40 w-full">
          <Reveal>
            <p className="text-[11px] md:text-xs tracking-[0.4em] uppercase text-[#C8A24B] mb-8">
              Sheffield &amp; Surrounding Areas · Built Around You
            </p>
          </Reveal>
          <Reveal delay={120}>
            <h1
              data-testid="hero-heading"
              className="font-display text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] max-w-5xl"
            >
              Joiners in Sheffield.
              <br />
              Bespoke <em className="text-[#C8A24B] not-italic font-light">Joinery &amp; Building</em>, crafted to last.
            </h1>
          </Reveal>
          <Reveal delay={260}>
            <p className="mt-8 max-w-xl text-white/80 text-lg leading-relaxed">
              Handcrafted staircases, extensions, fitted furniture and full
              renovations — delivered by a team that treats every home like their own.
            </p>
          </Reveal>
          <Reveal delay={380}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-quote"
                className="btn-primary"
              >
                Get a Free Quote <ArrowUpRight className="h-4 w-4" />
              </Link>
              <a
                href={CONTACT.sean.tel}
                data-testid="hero-cta-call"
                className="btn-ghost-light"
              >
                <Phone className="h-4 w-4" /> Call Sean
              </a>
            </div>
          </Reveal>
        </div>

        {/* Scroll cue */}
        <div className="absolute bottom-8 right-8 hidden md:flex items-center gap-3 text-white/60 text-[11px] tracking-[0.3em] uppercase">
          <div className="h-px w-10 bg-[#C8A24B]" />
          Scroll
        </div>
      </section>

      {/* Intro strip */}
      <section className="cream-grain py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-20 items-start">
          <Reveal className="md:col-span-5">
            <span className="gold-rule" />
            <p className="mt-4 text-[11px] tracking-[0.35em] uppercase text-[#4A5550]">
              Woodland Joinery &amp; Building
            </p>
            <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl text-[#1F3B2D] leading-tight">
              Timber, brick and honest work.
            </h2>
          </Reveal>
          <Reveal delay={180} className="md:col-span-6 md:col-start-7">
            <p className="text-lg leading-relaxed text-[#4A5550]">
              We are Sean and Rocky — a Sheffield-based joinery and building
              team with a shared obsession for getting the little things right.
              From a single bespoke shelf to a full rear extension, we design,
              build and finish every project with the care of tradesmen who
              actually enjoy their work.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-6">
              {[
                { n: "15+", l: "Years' experience" },
                { n: "200+", l: "Projects delivered" },
                { n: "100%", l: "Fully insured" },
              ].map((s, i) => (
                <div key={i} data-testid={`stat-${i}`}>
                  <div className="font-display text-3xl md:text-4xl text-[#1F3B2D]">
                    {s.n}
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-widest text-[#4A5550]">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Services bento */}
      <section
        data-testid="home-services"
        className="bg-[#FDFBF7] py-24 md:py-32 border-t border-[#E5E0D8]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
            <div>
              <span className="gold-rule" />
              <p className="mt-4 text-[11px] tracking-[0.35em] uppercase text-[#4A5550]">
                What we do
              </p>
              <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl text-[#1F3B2D]">
                Six trades. One trusted team.
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="home-services-view-all"
              className="link-underline text-sm tracking-wide text-[#1F3B2D]"
            >
              View all services →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
            {SERVICES.map((s, i) => {
              // Bento layout: alternate spans
              const spans = [
                "md:col-span-4",
                "md:col-span-2",
                "md:col-span-2",
                "md:col-span-4",
                "md:col-span-3",
                "md:col-span-3",
              ];
              return (
                <Reveal
                  key={s.slug}
                  delay={i * 90}
                  className={`${spans[i]} group`}
                >
                  <Link
                    to="/services"
                    data-testid={`home-service-card-${s.slug}`}
                    className="block relative overflow-hidden bg-white border border-[#E5E0D8] hover:border-[#C8A24B] transition-all duration-500 h-full"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={s.image}
                        alt={s.title}
                        className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      <span className="absolute top-4 left-4 text-[10px] tracking-[0.3em] uppercase text-[#FDFBF7] bg-[#1F3B2D]/80 px-3 py-1">
                        {s.tag}
                      </span>
                    </div>
                    <div className="p-8 flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-display text-2xl md:text-3xl text-[#1F3B2D]">
                          {s.title}
                        </h3>
                        <p className="mt-3 text-[15px] text-[#4A5550] leading-relaxed max-w-md">
                          {s.short}
                        </p>
                      </div>
                      <ArrowUpRight className="h-5 w-5 text-[#C8A24B] shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section
        data-testid="home-testimonials"
        className="bg-[#1F3B2D] text-white py-24 md:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <span className="gold-rule" />
            <p className="mt-4 text-[11px] tracking-[0.35em] uppercase text-[#C8A24B]">
              Kind words
            </p>
            <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl">
              From Sheffield homes to yours.
            </h2>
          </Reveal>
          <div className="mt-16 grid md:grid-cols-2 gap-10 md:gap-14">
            {TESTIMONIALS.slice(0, 4).map((t, i) => (
              <Reveal
                key={i}
                delay={i * 120}
                className="border-t border-white/10 pt-8"
              >
                <div className="font-display text-6xl leading-none text-[#C8A24B]">“</div>
                <p className="mt-4 text-xl md:text-2xl font-display font-light leading-relaxed text-white/90">
                  {t.quote}
                </p>
                <div className="mt-6 text-sm text-white/60">
                  <span className="text-[#C8A24B] tracking-wider uppercase text-[11px]">
                    {t.author}
                  </span>
                  <span className="mx-3 opacity-40">·</span>
                  {t.location}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        data-testid="home-cta"
        className="bg-[#FDFBF7] py-24 md:py-32 border-t border-[#E5E0D8]"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <span className="gold-rule" />
            <p className="mt-4 text-[11px] tracking-[0.35em] uppercase text-[#4A5550]">
              Ready when you are
            </p>
            <h2 className="mt-6 font-display text-4xl md:text-6xl text-[#1F3B2D] leading-tight">
              Let’s build something that lasts.
            </h2>
            <p className="mt-6 text-lg text-[#4A5550] max-w-2xl mx-auto">
              Free quotes across Sheffield and the surrounding areas. Tell us
              about your project — we’ll be back to you within 24 hours.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                data-testid="home-cta-quote"
                className="btn-primary"
              >
                Request Free Quote <ArrowUpRight className="h-4 w-4" />
              </Link>
              <a
                href={CONTACT.rocky.tel}
                data-testid="home-cta-call-rocky"
                className="btn-secondary"
              >
                <Phone className="h-4 w-4" /> Call Rocky
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
